import { Badge, Card } from "@vb/ui";

/** Tag styles per game — short letter codes so the rail stays tight. */
const GAME_BADGES: Record<
  "vault-breaker" | "pressure-plate" | "cipher-crack",
  { label: string; bg: string; fg: string }
> = {
  "vault-breaker": { label: "VB", bg: "rgba(245,197,109,0.15)", fg: "#F5C56D" },
  "pressure-plate": { label: "PP", bg: "rgba(0,229,255,0.15)", fg: "#5DD3E8" },
  "cipher-crack": { label: "CC", bg: "rgba(120,255,140,0.13)", fg: "#7CD9A0" },
};

export interface FeedRow {
  name: string;
  bet: number;
  multi: number | null;
  /** Which game produced this round. Optional for back-compat with rows
   *  emitted before multi-game rollout (treated as vault-breaker by default). */
  gameSlug?: "vault-breaker" | "pressure-plate" | "cipher-crack";
}

interface LivePlayerFeedProps {
  rows: FeedRow[];
  roundTag: string;
  maxHeight?: number;
}

const th: React.CSSProperties = {
  padding: "6px 10px",
  textAlign: "left",
  fontFamily: "var(--font-ui)",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--fg-3)",
  borderBottom: "1px solid var(--line-strong)",
};
const td: React.CSSProperties = {
  padding: "5px 10px",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--fg-2)",
};

export function LivePlayerFeed({ rows, roundTag, maxHeight = 400 }: LivePlayerFeedProps) {
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Badge kind="live" style={{ padding: "2px 8px", fontSize: 9 }}>
            LIVE
          </Badge>
          <span className="label" style={{ fontSize: 10 }}>
            Players · {rows.length.toLocaleString()}
          </span>
        </div>
        <span className="mono" style={{ color: "var(--fg-3)", fontSize: 10 }}>
          R{roundTag}
        </span>
      </div>
      <div className="vb-feed-scroll" style={{ maxHeight, overflowY: "auto" }}>
        {rows.length === 0 ? (
          <div
            style={{
              padding: "32px 12px",
              textAlign: "center",
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              color: "var(--fg-3)",
            }}
          >
            Listening for live rounds across all operators…
          </div>
        ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Player</th>
              <th className="vb-feed-bet-col" style={th}>Bet</th>
              <th style={th}>Multi</th>
              <th style={{ ...th, textAlign: "right" }}>Payout</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const tag = GAME_BADGES[r.gameSlug ?? "vault-breaker"];
              return (
              <tr key={i} style={{ borderTop: "1px solid var(--line)" }}>
                <td style={td}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: 999,
                      background: "linear-gradient(135deg,#242C3C,#1B2230)",
                      border: "1px solid var(--line)",
                      marginRight: 6,
                      verticalAlign: "middle",
                    }}
                  />
                  {r.name}
                  <span
                    aria-label={`Game ${tag.label}`}
                    style={{
                      marginLeft: 6,
                      padding: "1px 5px",
                      fontFamily: "var(--font-mono)",
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      borderRadius: 3,
                      background: tag.bg,
                      color: tag.fg,
                      verticalAlign: "middle",
                    }}
                  >
                    {tag.label}
                  </span>
                </td>
                <td className="vb-feed-bet-col" style={td}>{r.bet.toFixed(4)}</td>
                <td
                  style={{
                    ...td,
                    color: r.multi
                      ? r.multi >= 10
                        ? "var(--gold-bright)"
                        : r.multi >= 2
                        ? "var(--cyan)"
                        : "var(--fg-2)"
                      : "var(--red)",
                  }}
                >
                  {r.multi ? `${r.multi.toFixed(2)}×` : "—"}
                </td>
                <td
                  style={{
                    ...td,
                    textAlign: "right",
                    color: r.multi ? "var(--green)" : "var(--red)",
                  }}
                >
                  {r.multi ? `+${(r.bet * r.multi - r.bet).toFixed(4)}` : `−${r.bet.toFixed(4)}`}
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
        )}
      </div>
    </Card>
  );
}
